import { lazy, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { loginRefreshToken } from './api/auth';
import CreateUserAccount from './components/accounts/accounts/view/users/add-user';
import EditAccountUser from './components/accounts/accounts/view/users/edit-user';
import AllAnnouncements from './components/all-announcements';
import DangerousGoods from './components/dangerous-goods';
import ConsolidationDetail from './components/lms-reporting/consolidation/consolidationDetail';
import ItemDetails from './components/lms-reporting/items/itemDetails';
import ManifestDetails from './components/lms-reporting/manifest/details';
import ForgotPassword from './components/login/forgotPassword';
import ResetPassword from './components/login/resetPassword';
import ManualShipmentContainer from './components/manual-shipments';
import SearchShipmentsContainer from './components/search-shipments';
import TermsAgreed from './components/terms-conditions/termsAgreed';
import UnauthorizedPage from './components/unauthorized/UnauthorizedPage';
import ProtectedRoute from './components/unauthorized/protectedRoute';
import { AbilityProvider } from './context/AbilityContext';
import { ShipFormProvider } from './context/shipForms';
import { logout, setTokenofApp } from './redux/app/appActions';
import { RootState } from './redux/rootReducer';
import defineAbilitiesFor from './utils/ability';
import { clearLocalStorage } from './utils/helper';
import Project from './components/project';
import MainLoader from './components/global-components/loader';
import ProjectView from './Pages/ProjectView';
import AdminAddManualShipment from './Pages/AdminAddManualShipment';
import JobHistory from './Pages/JobHistory';
import ShipmentHistory from './Pages/ShipmentHistory';
import LegsHistory from './Pages/LegsHistory';
import Reports from './Pages/Reports';

const SetLanesContainer = lazy(() => import('./components/set-lanes'));
const AnnouncmentContainer = lazy(
  () => import('./components/admin/announcements'),
);
const Signup = lazy(() => import('./components/signup'));
const EmailVerification = lazy(() => import('./components/verify-email'));
const LmsLineItemContainer = lazy(
  () => import('./components/lms-reporting/line-item'),
);
const LmsItemsContainer = lazy(
  () => import('./components/lms-reporting/items'),
);
const LmsManifestContainer = lazy(
  () => import('./components/lms-reporting/manifest'),
);
const LmsConsolidationContainer = lazy(
  () => import('./components/lms-reporting/consolidation'),
);
const OverdueView = lazy(() => import('./components/overdue/overdueView'));
const JobView = lazy(() => import('./components/job-history/jobView'));
const ProjectViewPOSummarized = lazy(
  () => import('./components/project/projectViewPOSummarized'),
);
const AddLegContainer = lazy(() => import('./components/add-leg'));

const AccountsContainer = lazy(() => import('./components/accounts/accounts'));
const CreateAccountContainer = lazy(
  () => import('./components/accounts/accounts/add-account/createAccount'),
);
const AccountViewContainer = lazy(
  () => import('./components/accounts/accounts/view'),
);
const ExchangeRatesContainer = lazy(
  () => import('./components/accounts/exchange-rates'),
);
const MarkupsContainer = lazy(() => import('./components/accounts/markups'));
const PromoCodesContainer = lazy(
  () => import('./components/accounts/promo-codes'),
);
const PackageTypesContainer = lazy(
  () => import('./components/admin/package-types'),
);
const WebhookContainer = lazy(() => import('./components/admin/web-hooks'));
const CarriersContainer = lazy(() => import('./components/carriers/carriers'));
const CarrierViewContainer = lazy(
  () => import('./components/carriers/carriers/view'),
);
const FuelSurchargeContainer = lazy(
  () => import('./Pages/FuelSurchargeContainer'),
);
const PickupRestrictionContainer = lazy(
  () => import('./components/carriers/pickup-restrictions'),
);
const PortsContainer = lazy(() => import('./components/carriers/ports'));
const APIsContainer = lazy(() => import('./components/dev/apis'));
const ErrorCodeContainer = lazy(() => import('./components/dev/error-codes'));
const RateSheetContainer = lazy(
  () => import('./components/carriers/rate-sheets'),
);
const SailingDatesContainer = lazy(
  () => import('./components/carriers/sailing-dates'),
);
const SailingViewContainer = lazy(
  () => import('./components/carriers/sailing-dates/view'),
);
const PermissionsContainer = lazy(() => import('./components/dev/permissions'));
const TiersContainer = lazy(() => import('./components/dev/tiers'));
const TiersViewContainer = lazy(() => import('./components/dev/tiers/view'));
const AccountsStatsContainer = lazy(
  () => import('./components/metrics/accounts-stats'),
);
const MetricBreakDownContainer = lazy(
  () => import('./components/metrics/metric-breakdown'),
);
const ShipmentView = lazy(
  () => import('./components/shipment-history/shipmentView'),
);
const SavedQuotesView = lazy(
  () => import('./components/saved-quotes/views/savedQuotesView'),
);
const SaveQuoteContainer = lazy(
  () => import('./components/quick-quote/save-quote'),
);
const SettingsContainer = lazy(() => import('./components/settings'));
const SavedProfiles = lazy(
  () => import('./components/saved-quotes/saved-profiles'),
);
const OverdueContainer = lazy(() => import('./components/overdue'));
const SavedBrokers = lazy(() => import('./components/books/saved-brokers'));
const SavedPackages = lazy(() => import('./components/books/saved-packages'));
const SavedContacts = lazy(() => import('./components/books/saved-contacts'));
const SavedAddresses = lazy(() => import('./components/books/saved-address'));
const SavedFees = lazy(() => import('./components/saved-quotes/savedFees'));
const SavedTerms = lazy(() => import('./components/saved-quotes/savedTerms'));
const SavedQuotes = lazy(() => import('./components/saved-quotes'));
const QuickContainer = lazy(() => import('./components/quick-quote'));
const ShipConfirmation = lazy(() => import('./components/ship-confirmation'));
const OldShipmentHistory = lazy(() => import('./components/shipment-history'));
const Dashboard = lazy(() => import('./components/home/dashboard'));
const ShipmentContainer = lazy(() => import('./components/shipment'));
const TrackContainer = lazy(() => import('./components/track'));
const Login = lazy(() => import('./components/login'));
const MyBBEPage = lazy(() => import('./components/my-bbe'));
const AddShipment = lazy(() => import('./Pages/AddShipment'));

function MainPage() {
  const { userData, hasSeenTerms } = useSelector(
    (state: RootState) => state.mainAppData,
  );
  const expiredTime = localStorage.getItem('accessExpiry');
  const refreshToken = localStorage.getItem('refreshToken');
  const dispatch = useDispatch<any>();
  const location = useLocation();
  const navigate = useNavigate();
  const [prevRoute, setPrevRoute] = useState('');
  const userRole =
    userData &&
    userData?.user_role_permissions &&
    userData?.user_role_permissions[0]?.role;
  const ability = defineAbilitiesFor(userRole, userData?.user_role_permissions);
  const isTermsAgreed = userData?.is_terms_agreed;
  const [isLoading, setIsLoading] = useState(true);

  if (location.pathname !== prevRoute) {
    clearLocalStorage();
    setPrevRoute(location.pathname);
  }

  if (
    location.pathname !== '/app/ship-now' &&
    location.pathname !== '/app/add-leg' &&
    location.pathname !== '/app/admin/manual-shipments' &&
    location.pathname !== '/app/quick-quote'
  ) {
    clearLocalStorage();
  }
  useEffect(() => {
    var currentTime = Date.now();
    var currentTimeStamp = Math.round(currentTime / 1000);
    if (expiredTime && currentTimeStamp > Number(expiredTime)) {
      window.scrollTo(0, 0);
      localStorage.setItem('authToken', '');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('accessExpiry');
      dispatch(setTokenofApp(''));
      dispatch(logout());
      navigate('/login');
    }

    var futureTime = currentTime + 10 * 60 * 1000;
    var futureTimeInSeconds = Math.round(futureTime / 1000);
    if (expiredTime && futureTimeInSeconds > Number(expiredTime)) {
      loginRefreshToken({ refresh: refreshToken })
        .then((response: any) => {
          if (response?.data?.is_error === false) {
            dispatch(setTokenofApp(response?.data?.content?.access));
            localStorage.setItem(
              'refreshToken',
              response?.data?.content?.refresh,
            );
            localStorage.setItem(
              'accessExpiry',
              response?.data?.content?.access_expiry,
            );
          }
        })
        .catch((e) => {});
    }
  }, []);
  useEffect(() => {
    const publicRoutes = [
      '/login',
      '/sign-up',
      '/reset_password',
      '/confirm_email',
      '/password_reset',
    ];
    const isPublicRoute = publicRoutes.includes(location.pathname);

    if (isPublicRoute) {
      setIsLoading(false);
    } else if (userData !== undefined && userData !== null) {
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
  }, [userData, location.pathname]);
  if (isLoading) return <MainLoader />;

  return (
    <AbilityProvider
      role={userRole}
      permissions={userData?.user_role_permissions}
    >
      <Routes>
        <Route
          path='*'
          element={<UnauthorizedPage />}
        />

        {!isTermsAgreed && (
          <Route
            path='app/terms_agreed'
            element={<TermsAgreed />}
          />
        )}
        <Route
          path='reset_password'
          element={<ForgotPassword />}
        />
        <Route
          path='login'
          element={<Login />}
        />
        <Route
          path='/sign-up'
          element={<Signup />}
        />
        <Route
          path='/confirm_email'
          element={<EmailVerification />}
        />
        <Route
          path='/password_reset'
          element={<ResetPassword />}
        />
        <Route
          path='unauthorized'
          element={<UnauthorizedPage />}
        />
        {(isTermsAgreed || hasSeenTerms) && (
          <>
            <Route
              path='app/track'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Track'
                  ability={ability}
                >
                  <TrackContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/ship-now/confirmation'
              element={<ShipConfirmation />}
            />
            <Route
              path='/app/settings'
              element={<SettingsContainer />}
            />
            <Route
              path='app/ship-now'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Ship'
                  ability={ability}
                >
                  <ShipmentContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path='app/add-shipment'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Add Shipment'
                  ability={ability}
                >
                  <AddShipment />
                </ProtectedRoute>
              }
            />
            <Route
              path='app/add-leg'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Add Leg'
                  ability={ability}
                >
                  <ShipFormProvider>
                    <AddLegContainer />
                  </ShipFormProvider>
                </ProtectedRoute>
              }
            />
            <Route
              path='app/admin/manual-shipments'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Admin Manual Shipment'
                  ability={ability}
                >
                  <ManualShipmentContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path='app/admin/add-manual-shipment'
              element={
                <ProtectedRoute
                  action='read'
                  subject='Admin Add Manual Shipment'
                  ability={ability}
                >
                  <AdminAddManualShipment />
                </ProtectedRoute>
              }
            />
            <Route
              path='app/quick-quote'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Quote'
                  ability={ability}
                >
                  <QuickContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path='app/shipment-history'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Shipments'
                  ability={ability}
                >
                  <ShipmentHistory />
                </ProtectedRoute>
              }
            />
            <Route
              path='app/admin/shipment-history'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Admin Shipments'
                  ability={ability}
                >
                  <ShipmentHistory isAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path='app/dangerous-goods'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Dangerous Goods'
                  ability={ability}
                >
                  <DangerousGoods />
                </ProtectedRoute>
              }
            />
            <Route
              path='app/leg-history'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Legs'
                  ability={ability}
                >
                  <LegsHistory />
                </ProtectedRoute>
              }
            />
            <Route
              path='app/admin/leg-history'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Admin Legs'
                  ability={ability}
                >
                  <LegsHistory isAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/shipment-history/view'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Shipment View'
                  ability={ability}
                >
                  <ShipmentView />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/admin/shipment-history/view'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Admin Shipments'
                  ability={ability}
                >
                  <ShipmentView />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/quote/saved/view'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Saved Quote View'
                  ability={ability}
                >
                  <SavedQuotesView />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/admin/quote/saved/view'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Saved Quote View'
                  ability={ability}
                >
                  <SavedQuotesView />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/project'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Project'
                  ability={ability}
                >
                  <Project />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/project/view'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Project'
                  ability={ability}
                >
                  <ProjectView />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/project/view_shipments'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Project'
                  ability={ability}
                >
                  <ProjectViewPOSummarized />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/job-history'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Job History'
                  ability={ability}
                >
                  <JobHistory />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/job-history/view'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Job History'
                  ability={ability}
                >
                  <JobView />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/admin/job-history'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Admin Job History'
                  ability={ability}
                >
                  <JobHistory isAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/admin/job-history/view'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Admin Job History'
                  ability={ability}
                >
                  <JobView />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/saved-quotes'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Saved Quotes'
                  ability={ability}
                >
                  <SavedQuotes />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/admin/saved-quotes'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Admin Saved Quotes'
                  ability={ability}
                >
                  <SavedQuotes />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/quotes/save-quote'
              element={
                <ProtectedRoute
                  action={'create'}
                  subject='Quote'
                  ability={ability}
                >
                  <SaveQuoteContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/quotes/terms'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Terms'
                  ability={ability}
                >
                  <SavedTerms />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/quotes/profiles'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Profiles'
                  ability={ability}
                >
                  <SavedProfiles />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/overdue'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Overdue'
                  ability={ability}
                >
                  <OverdueContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/admin/overdue'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Admin Overdue'
                  ability={ability}
                >
                  <OverdueContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/overdue/view'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Overdue'
                  ability={ability}
                >
                  <OverdueView />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/admin/overdue/view'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Admin Overdue'
                  ability={ability}
                >
                  <OverdueView />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/reports'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Reports'
                  ability={ability}
                >
                  <Reports />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/admin/reports'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Admin Reports'
                  ability={ability}
                >
                  <Reports isAdmin={true} />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/quotes/fees'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Fees'
                  ability={ability}
                >
                  <SavedFees />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/saved/address'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Saved Address'
                  ability={ability}
                >
                  <SavedAddresses />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/saved/contacts'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Saved Contact'
                  ability={ability}
                >
                  <SavedContacts />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/saved/brokers'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Saved Broker'
                  ability={ability}
                >
                  <SavedBrokers />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/saved/package'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Saved Packages'
                  ability={ability}
                >
                  <SavedPackages />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/admin/package_types'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Admin Package Types'
                  ability={ability}
                >
                  <PackageTypesContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/admin/webhooks'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Admin Webhooks'
                  ability={ability}
                >
                  <WebhookContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/admin/set-lanes'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Admin Set Lanes'
                  ability={ability}
                >
                  <SetLanesContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/admin/announcements'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Admin Announcements'
                  ability={ability}
                >
                  <AnnouncmentContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/admin/accounts'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Admin Accounts'
                  ability={ability}
                >
                  <AccountsContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/admin/accounts/view'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Admin Accounts'
                  ability={ability}
                >
                  <AccountViewContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/admin/accounts/add'
              element={
                <ProtectedRoute
                  action={'create'}
                  subject='Admin Accounts'
                  ability={ability}
                >
                  <CreateAccountContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/admin/user/add'
              element={
                <ProtectedRoute
                  action={'create'}
                  subject='Admin Accounts'
                  ability={ability}
                >
                  <CreateUserAccount />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/admin/user/view'
              element={
                <ProtectedRoute
                  action={'create'}
                  subject='Admin Accounts'
                  ability={ability}
                >
                  <EditAccountUser />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/admin/markups'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Admin Markups'
                  ability={ability}
                >
                  <MarkupsContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/admin/exchange_rates'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Admin Exchange Rates'
                  ability={ability}
                >
                  <ExchangeRatesContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/admin/promo_codes'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Admin Promo Codes'
                  ability={ability}
                >
                  <PromoCodesContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/admin/accounts/stats'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Admin Acount Stats'
                  ability={ability}
                >
                  <AccountsStatsContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/admin/metrics/breakdown'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Admin Shipment Breakdown'
                  ability={ability}
                >
                  <MetricBreakDownContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/admin/carriers'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Admin Carriers'
                  ability={ability}
                >
                  <CarriersContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/admin/carriers/view'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Admin Carriers'
                  ability={ability}
                >
                  <CarrierViewContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/admin/fuel_surcharge'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Admin Fuel Surcharge'
                  ability={ability}
                >
                  <FuelSurchargeContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/admin/rate_sheets'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Admin Ratesheets'
                  ability={ability}
                >
                  <RateSheetContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/admin/carrier/pickup_restriction'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Admin Pickup Restrictions'
                  ability={ability}
                >
                  <PickupRestrictionContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/admin/ports'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Admin Port'
                  ability={ability}
                >
                  <PortsContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/admin/sailing_dates'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Admin Sailings'
                  ability={ability}
                >
                  <SailingDatesContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/admin/sailing_dates/view'
              element={
                <ProtectedRoute
                  action={'edit'}
                  subject='Admin Sailings'
                  ability={ability}
                >
                  <SailingViewContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/admin/api'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Dev Apis'
                  ability={ability}
                >
                  <APIsContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/admin/permissions'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Dev Permissions'
                  ability={ability}
                >
                  <PermissionsContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/admin/tiers'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Dev Tiers'
                  ability={ability}
                >
                  <TiersContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/admin/tiers/view'
              element={
                <ProtectedRoute
                  action={'edit'}
                  subject='Dev Tiers'
                  ability={ability}
                >
                  <TiersViewContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/admin/error'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Dev Error Codes'
                  ability={ability}
                >
                  <ErrorCodeContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/dashboard'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Dashboard'
                  ability={ability}
                >
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/home'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='My BBE Page'
                  ability={ability}
                >
                  <MyBBEPage />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/announcements'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Announcements'
                  ability={ability}
                >
                  <AllAnnouncements />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/admin/dashboard'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Admin Dashboard'
                  ability={ability}
                >
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/admin/search_shipments'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='Dashboard'
                  ability={ability}
                >
                  <SearchShipmentsContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/warehouse/line-item'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='LMS Line Items'
                  ability={ability}
                >
                  <LmsLineItemContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/warehouse/items'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='LMS Items'
                  ability={ability}
                >
                  <LmsItemsContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/warehouse/items/view'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='LMS Items'
                  ability={ability}
                >
                  <ItemDetails />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/warehouse/manifest'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='LMS Manifest'
                  ability={ability}
                >
                  <LmsManifestContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/warehouse/manifest/details'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='LMS Manifest'
                  ability={ability}
                >
                  <ManifestDetails />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/warehouse/consolidation'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='LMS Consolidation'
                  ability={ability}
                >
                  <LmsConsolidationContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/warehouse/consolidation/view'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='LMS Consolidation'
                  ability={ability}
                >
                  <ConsolidationDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path='/app/warehouse/reports'
              element={
                <ProtectedRoute
                  action={'read'}
                  subject='LMS Reports'
                  ability={ability}
                >
                  <Reports isLms={true} />
                </ProtectedRoute>
              }
            />
          </>
        )}
      </Routes>
    </AbilityProvider>
  );
}

export default MainPage;
